import type { ReactElement } from "react";

import { useSelector } from "react-redux";

import { Flex } from "@similarweb/ui-components/foundations";

import { useContactsStatusEffect } from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useContactsStatusEffect";
import { selectQuotaIsBeingFetched } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";

import ErrorBoundaryWithLogging from "../../../common-components/ErrorBoundaryWithLogging/ErrorBoundaryWithLogging";
import TopCompaniesEmptyState from "../components/TopCompaniesEmptyState/TopCompaniesEmptyState";
import TopCompaniesLoading from "../components/TopCompaniesLoading/TopCompaniesLoading";
import useTopCompaniesListQuery from "../query/queries/useTopCompaniesListQuery";
import useCompanyOpportunitySpottedQuery from "../query/queries/useTopCompaniesOpportunitySpottedQuery";

import TopCompaniesCardContainer from "./TopCompaniesCardContainer";

const TopCompanies = (): ReactElement => {
    const isQuotaLoading = useSelector(selectQuotaIsBeingFetched);

    useContactsStatusEffect();

    const {
        data: topCompaniesList,
        isLoading: isTopCompaniesListLoading,
        isFetching: isTopCompaniesListFetching,
        isError,
        refetch,
    } = useTopCompaniesListQuery();

    const domains: readonly string[] = (topCompaniesList || []).map(({ domain }) => domain);

    const { data: opportunitySpottedData, isFetching: isOpportunitySpottedFetching } =
        useCompanyOpportunitySpottedQuery(domains);

    if (isTopCompaniesListLoading || isQuotaLoading) {
        return <TopCompaniesLoading />;
    }

    if (isError) {
        return <TopCompaniesEmptyState isError />;
    }

    if (!topCompaniesList || topCompaniesList.length === 0) {
        return <TopCompaniesEmptyState />;
    }

    return (
        <ErrorBoundaryWithLogging fallback={<TopCompaniesEmptyState isError />}>
            <Flex
                flexDirection="column"
                gap="16px"
                width="100%"
            >
                {topCompaniesList.map((topCompanyItem) => (
                    <TopCompaniesCardContainer
                        key={topCompanyItem.domain + topCompanyItem?.selectedContact?.id}
                        company={topCompanyItem}
                        isLoading={isTopCompaniesListFetching}
                        isOpportunitySpottedFetching={isOpportunitySpottedFetching}
                        opportunitySpottedText={opportunitySpottedData?.get(topCompanyItem.domain)}
                        onRefetchList={refetch}
                    />
                ))}
            </Flex>
        </ErrorBoundaryWithLogging>
    );
};

export default TopCompanies;
